import { ProductDetails, Filters, Sort } from '../models/product';

/**
 * Filters products by price range, categories, and search text.
 * Pure function - safe for unit testing.
 */
export const filterProducts = (products: ProductDetails[], filters: Filters, searchText: string): ProductDetails[] => {
  return products.filter((product) => {
    const minPrice = filters.minimumPrice ? Number(filters.minimumPrice) : null;
    const maxPrice = filters.maximumPrice ? Number(filters.maximumPrice) : null;
    const priceInRange =
      (minPrice === null || product.price >= minPrice) &&
      (maxPrice === null || product.price <= maxPrice);

    const hasCategorySelected = Object.values(filters.categories).some(Boolean);
    const categoryMatch =
      !hasCategorySelected ||
      (product.categories?.some((cat) => filters.categories[cat]) ?? false);

    const searchMatch =
      !searchText ||
      product.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (product.description?.toLowerCase().includes(searchText.toLowerCase()) ?? false) ||
      (product.categories?.join(' ').toLowerCase().includes(searchText.toLowerCase()) ?? false);

    return priceInRange && categoryMatch && searchMatch;
  });
};

/**
 * Sorts products by the given sort option.
 * Pure function - safe for unit testing.
 */
export const sortProducts = (products: ProductDetails[], sort: Sort): ProductDetails[] => {
  return [...products].sort((a, b) => {
    if (sort === 'Price: Low to High') return a.price - b.price;
    if (sort === 'Price: High to Low') return b.price - a.price;
    if (sort === 'Product name: A to Z') return a.name.localeCompare(b.name);
    if (sort === 'Product name: Z to A') return b.name.localeCompare(a.name);
    return 0;
  });
};

/**
 * Filters and sorts products. Convenience function combining filterProducts and sortProducts.
 */
export const filterAndSortProducts = (
  products: ProductDetails[],
  filters: Filters,
  searchText: string,
  sort: Sort
): ProductDetails[] => {
  const filtered = filterProducts(products, filters, searchText);
  return sortProducts(filtered, sort);
};
